import React, { useState } from 'react';
import { useWeb3 } from './context/Web3Context';
import { useUserData } from './hooks/useUserData';
import { useRole } from './hooks/useRole';
import { useBlacklist } from './hooks/useBlacklist';
import { shortAddr } from './utils/format';

import Loader from './components/common/Loader';
import Logo from './components/common/Logo';
import BottomNav from './components/common/BottomNav';
import ConnectWallet from './components/ConnectWallet';
import AccessRestricted from './components/AccessRestricted';
import NetworkBanner from './components/NetworkGuard';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Packages from './components/Packages';
import Pools from './components/Pools';
import Team from './components/Team';
import Activity from './components/Activity';
import AdminPanel from './components/admin/AdminPanel';

export default function App() {
  const { isConnected, account, correctChain, switching, disconnect } = useWeb3();
  const data = useUserData();
  const role = useRole();
  const blacklist = useBlacklist();
  const [tab, setTab] = useState('home');

  // Not connected -> connect screen
  if (!isConnected) {
    return (
      <>
        <div className="app-bg" />
        <ConnectWallet />
      </>
    );
  }

  // First data load -> full loader
  if (!data.loaded) {
    return (
      <>
        <div className="app-bg" />
        <Loader text="EVITTA" sub="Syncing your account…" />
      </>
    );
  }

  // Blacklist gate — block suspended accounts (admins/owner are never blocked).
  if (blacklist.loaded && !role.isAdmin && blacklist.isBlacklisted(account, data.userId)) {
    return (
      <>
        <div className="app-bg" />
        <AccessRestricted />
      </>
    );
  }

  const renderTab = () => {
    // Admin/Owner panel is reachable regardless of user registration.
    if (tab === 'admin' && role.isAdmin) return <AdminPanel role={role} />;

    // Connected but not registered -> registration flow.
    if (!data.registered) {
      return <Register data={data} onRegistered={data.refresh} />;
    }
    switch (tab) {
      case 'home': return <Dashboard data={data} />;
      case 'packages': return <Packages data={data} />;
      case 'pools': return <Pools data={data} />;
      case 'team': return <Team data={data} />;
      case 'activity': return <Activity data={data} />;
      default: return <Dashboard data={data} />;
    }
  };

  const showNav = data.registered || role.isAdmin;

  return (
    <>
      <div className="app-bg" />

      {/* Network switch overlay — stays mounted, NEVER refreshes the page */}
      {switching && <Loader overlay text="EVITTA" sub="Switching network…" />}

      <div className="app-shell">
        <header className="topbar">
          <Logo size={32} withWordmark />
          <div className="wallet-chip" onClick={disconnect} title="Tap to disconnect">
            <span className={`dot ${correctChain ? '' : 'off'}`} />
            {shortAddr(account)}
          </div>
        </header>

        <NetworkBanner />

        <main>{renderTab()}</main>
      </div>

      {showNav && <BottomNav active={tab} onChange={setTab} showAdmin={role.isAdmin} />}
    </>
  );
}
